import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Typography,
  MenuItem,
  Stack,
} from "@mui/material";

import { SosAlertService } from "../../../services/SosAlertService";
import { CkSosTicketStatusEnum } from "../../../enums/CkSosTicketStatusEnum";
import { ActionTypeEnum } from "../../../enums/ActionTypeEnum";
import ReasonSubreasonModuleEnum from "../../../enums/ReasonSubreasonModuleEnum";
import { getReasonCategory } from "../constants/constant";

const sosAlertService = new SosAlertService();
const OTHER_OPTION = "Other";

type Props = {
  open: boolean;
  onClose: () => void;
  sosId: number;
  currentStatus?: string;
  referenceModelName?: string;
  alertReason?: string;
  onSuccess?: () => void;
  title?: string;
};

const STATUS_OPTIONS: { value: CkSosTicketStatusEnum; label: string }[] = [
  { value: CkSosTicketStatusEnum.OPEN, label: "Open" },
  { value: CkSosTicketStatusEnum.IN_PROGRESS, label: "In Progress" },
  { value: CkSosTicketStatusEnum.RESOLVED, label: "Resolved" },
  { value: CkSosTicketStatusEnum.CLOSED, label: "Closed" },
];

type ReasonOption = { category: string; subCategory: string[] };

const resolveModule = (referenceModelName?: string): string | null => {
  if (referenceModelName === "ONE_MEAL_SERVICE")
    return ReasonSubreasonModuleEnum.ONE_MEAL_SERVICE;
  if (referenceModelName === "CHEF_FOR_PARTY")
    return ReasonSubreasonModuleEnum.CHEF_FOR_PARTY;
  return null;
};

const SosUpdateDialog = ({
  open,
  onClose,
  sosId,
  currentStatus,
  referenceModelName,
  alertReason,
  onSuccess,
  title = "Update SOS Ticket",
}: Props) => {
  const [status, setStatus] = useState<string>(currentStatus || "");
  const [reasonOptions, setReasonOptions] = useState<ReasonOption[]>([]);
  const [subReason, setSubReason] = useState<string>("");
  const [otherText, setOtherText] = useState<string>("");
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStatus(currentStatus || "");
    setSubReason("");
    setOtherText("");
    setError(null);

    const module = resolveModule(referenceModelName);
    if (!module) {
      setReasonOptions([]);
      return;
    }

    let cancelled = false;
    setReasonsLoading(true);
    getReasonCategory(module, ActionTypeEnum.SOS_COMMENTS)
      .then((res: ReasonOption[]) => {
        if (!cancelled) setReasonOptions(res || []);
      })
      .catch(() => {
        if (!cancelled) setReasonOptions([]);
      })
      .finally(() => {
        if (!cancelled) setReasonsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, currentStatus, referenceModelName]);

  const subReasonChoices = useMemo(() => {
    if (!alertReason) return [] as string[];
    const match = reasonOptions.find((opt) => opt.category === alertReason);
    const base = match?.subCategory ?? [];
    return [...base, OTHER_OPTION];
  }, [reasonOptions, alertReason]);

  const isOtherSelected = subReason === OTHER_OPTION;
  const statusChanged = !!status && status !== currentStatus;
  const showComment =
    status === CkSosTicketStatusEnum.RESOLVED ||
    status === CkSosTicketStatusEnum.CLOSED;
  const hasComment =
    showComment &&
    !!subReason &&
    (!isOtherSelected || otherText.trim().length > 0);
  const canSubmit = statusChanged || hasComment;

  const buildComment = () => {
    if (!subReason) return "";
    if (isOtherSelected) return otherText.trim();
    return subReason;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setLoading(true);
    setError(null);

    try {
      if (statusChanged) {
        await sosAlertService.updateSosTicketStatus(sosId, { status });
      }
      if (hasComment) {
        await sosAlertService.addSosComment(sosId, {
          commentOrFeedback: buildComment(),
        });
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError("Failed to update SOS ticket. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            select
            fullWidth
            label="Status"
            value={status}
            disabled={loading}
            onChange={(e) => {
              const next = e.target.value;
              setStatus(next);
              if (
                next !== CkSosTicketStatusEnum.RESOLVED &&
                next !== CkSosTicketStatusEnum.CLOSED
              ) {
                setSubReason("");
                setOtherText("");
              }
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          {showComment && (
            <TextField
              select
              fullWidth
              label="Comment"
              value={subReason}
              disabled={loading || reasonsLoading || !alertReason}
              helperText={
                !alertReason
                  ? "Alert reason missing — cannot list comments"
                  : reasonsLoading
                  ? "Loading comments..."
                  : undefined
              }
              onChange={(e) => {
                setSubReason(e.target.value);
                setOtherText("");
              }}
            >
              {subReasonChoices.map((sub) => (
                <MenuItem key={sub} value={sub}>
                  {sub}
                </MenuItem>
              ))}
            </TextField>
          )}

          {showComment && isOtherSelected && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Specify comment"
              value={otherText}
              disabled={loading}
              onChange={(e) => setOtherText(e.target.value)}
            />
          )}

          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !canSubmit}
        >
          {loading ? <CircularProgress size={20} /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SosUpdateDialog;
